import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine.ts';
import { type SharedEventLoop } from '../../event-loop.ts';
import { type SelectionUiInteractionHandler } from '../../selection-ui-interaction.ts';
import { type SelectionInput } from '../../types.ts';
import { type TableNavigationAdapter } from '../table-navigation-adapter.ts';
import { isEditableTarget } from '../utils/is-editable-target.ts';

import { createTableNavigationKeyDownHandler } from './handle-table-navigation-key-down.ts';

/**
 * Creates the table arrow/navigation keyboard input.
 *
 * This input subscribes to the synchronous keyboard loop so that
 * `preventDefault` runs in the same event-dispatch frame as the native
 * keydown. Without this, Cmd+Arrow would fire browser back/forward
 * navigation before the RAF-batched handler could cancel it.
 */
export function createTableNavigationInput(
  machine: SelectionsMachine,
  sharedLoop: SharedEventLoop,
  options: {
    adapter: TableNavigationAdapter;
    onSelectionUiInteraction?: SelectionUiInteractionHandler;
  }
): SelectionInput {
  const handler = createTableNavigationKeyDownHandler(
    options.adapter,
    options.onSelectionUiInteraction
  );
  let unsubscribe: (() => void) | undefined;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribeSyncKeyboardDown(
        (capture: KeyboardDownCapture) => {
          if (capture.properties.consumed) {
            return;
          }

          if (isEditableTarget(capture.properties.event.target)) {
            return;
          }

          handler(capture, machine);
        }
      );
    },

    dispose() {
      unsubscribe?.();
    }
  };
}
